import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface DataEntry {
  objective?: string;
  sublevels: string;
  words: string[];
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: DataEntry[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;
const MAX_LIVES = 3;

const OBJECTIVE_PT: Record<string, string> = {
  'Numbers up to 3.': 'Números até 3.',
  'Numbers up to 4.': 'Números até 4.',
  'Numbers up to 5.': 'Números até 5.',
  'Numbers up to 6.': 'Números até 6.',
  'Numbers up to 7.': 'Números até 7.',
  'Numbers up to 8.': 'Números até 8.',
  'Numbers up to 9.': 'Números até 9.',
  'Numbers from 0 to 9.': 'Números de 0 a 9.',
};

function fallDurationFor(levelNumber: number): number {
  if (levelNumber <= 2) return 6;
  if (levelNumber <= 5) return 5;
  return 4;
}

function maxNumberOfLevel(level: Level): number {
  let max = 0;
  for (const entry of level.data) {
    for (const w of entry.words) {
      const n = parseInt(w, 10);
      if (n > max) max = n;
    }
  }
  return max;
}

function minNumberOfLevel(level: Level): number {
  let min = Infinity;
  for (const entry of level.data) {
    for (const w of entry.words) {
      const n = parseInt(w, 10);
      if (n < min) min = n;
    }
  }
  return isFinite(min) ? min : 0;
}

function pickValueFromEntry(entry: DataEntry): number {
  const idx = Math.floor(Math.random() * entry.words.length);
  return parseInt(entry.words[idx] ?? '1', 10);
}

function splitDomino(total: number): [number, number] {
  if (total === 0) return [0, 0];
  const max = Math.min(total, 9);
  const a = Math.floor(Math.random() * Math.min(max, 6) + 0);
  const left = total - a;
  if (left < 0 || left > 9) {
    return splitDomino(total);
  }
  return [a, left];
}

const DOT_PATTERNS: Record<number, [number, number][]> = {
  0: [],
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [0, 2], [2, 0], [2, 2]],
  5: [[0, 0], [0, 2], [1, 1], [2, 0], [2, 2]],
  6: [[0, 0], [0, 2], [1, 0], [1, 2], [2, 0], [2, 2]],
  7: [[0, 0], [0, 2], [1, 0], [1, 1], [1, 2], [2, 0], [2, 2]],
  8: [[0, 0], [0, 1], [0, 2], [1, 0], [1, 2], [2, 0], [2, 1], [2, 2]],
  9: [[0, 0], [0, 1], [0, 2], [1, 0], [1, 1], [1, 2], [2, 0], [2, 1], [2, 2]],
};

function buildDotsGrid(value: number): string {
  const positions = DOT_PATTERNS[Math.min(value, 9)] ?? [];
  const cells: string[] = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const has = positions.some((p) => p[0] === r && p[1] === c);
      cells.push(has ? '<div class="smnum__dot"></div>' : '<div></div>');
    }
  }
  return cells.join('');
}

export function mountSmallNumbers2(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const fallSec = fallDurationFor(level.level);
  const min = minNumberOfLevel(level);
  const max = maxNumberOfLevel(level);
  const totalSubs = level.data.reduce((acc, d) => acc + parseInt(d.sublevels, 10), 0);
  const objectivePt = OBJECTIVE_PT[level.objective] ?? level.objective;

  let dataIdx = 0;
  let subWithinData = 0;
  let lives = MAX_LIVES;
  let correctCount = 0;
  let currentValue = 0;
  let inputBlocked = true;
  let transitionEndHandler: ((e: TransitionEvent) => void) | null = null;
  let dominoEl: HTMLElement | null = null;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Conte os pontos</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage smnum">
      <div class="feedback" aria-live="polite"></div>
      <div class="smnum__header">
        <p class="smnum__progress" aria-live="polite"></p>
        <span class="smnum__lives"></span>
      </div>
      <p class="smnum__hint">${objectivePt}</p>
      <div class="smnum__playfield">
        <div class="smnum__ground"></div>
      </div>
      <div class="smnum__keypad"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.smnum__progress')!;
  const livesEl = page.querySelector<HTMLElement>('.smnum__lives')!;
  const playfield = page.querySelector<HTMLElement>('.smnum__playfield')!;
  const keypad = page.querySelector<HTMLElement>('.smnum__keypad')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  const onKey = (e: KeyboardEvent): void => {
    if (inputBlocked) return;
    if (e.key >= '0' && e.key <= '9') {
      submitGuess(parseInt(e.key, 10));
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', onKey);

  function renderKeypad(): void {
    keypad.innerHTML = '';
    for (let n = min; n <= max; n++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'smnum__key';
      btn.textContent = String(n);
      btn.addEventListener('click', () => submitGuess(n));
      keypad.appendChild(btn);
    }
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${correctCount + 1} de ${totalSubs}`;
    livesEl.textContent = '❤️'.repeat(lives) + '🤍'.repeat(MAX_LIVES - lives);
  }

  function clearDomino(): void {
    if (dominoEl && transitionEndHandler) {
      dominoEl.removeEventListener('transitionend', transitionEndHandler);
    }
    if (dominoEl && dominoEl.parentElement) dominoEl.parentElement.removeChild(dominoEl);
    dominoEl = null;
    transitionEndHandler = null;
  }

  function spawnDomino(): void {
    clearDomino();
    const entry = level.data[dataIdx]!;
    currentValue = pickValueFromEntry(entry);
    const [a, b] = splitDomino(currentValue);
    const el = document.createElement('div');
    el.className = 'smnum__domino';
    el.style.setProperty('--fall-duration', `${fallSec}s`);
    el.innerHTML = `
      <div class="smnum__half">${buildDotsGrid(a)}</div>
      <div class="smnum__half">${buildDotsGrid(b)}</div>
    `;
    playfield.appendChild(el);
    dominoEl = el;
    const fieldH = playfield.clientHeight;
    const dominoH = 64;
    const ground = 14;
    const distance = fieldH - dominoH - ground;
    // Force reflow then animate
    void el.offsetWidth;
    el.classList.add('smnum__domino--falling');
    el.style.transform = `translate(-50%, ${distance}px)`;
    transitionEndHandler = (e: TransitionEvent) => {
      if (e.propertyName !== 'transform') return;
      if (inputBlocked) return; // already handled by answer
      onDominoGround();
    };
    el.addEventListener('transitionend', transitionEndHandler);
    inputBlocked = false;
  }

  function submitGuess(guess: number): void {
    if (inputBlocked) return;
    inputBlocked = true;
    if (guess === currentValue) {
      ctx.audio.play('correct');
      showFeedback('ok');
      if (dominoEl) dominoEl.classList.add('smnum__domino--correct');
      correctCount++;
      advance();
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      if (dominoEl) dominoEl.classList.add('smnum__domino--wrong');
      lives--;
      window.setTimeout(() => {
        if (lives <= 0) onLose();
        else advanceAfterMistake();
      }, 700);
    }
  }

  function onDominoGround(): void {
    inputBlocked = true;
    ctx.audio.play('wrong');
    showFeedback('err');
    if (dominoEl) dominoEl.classList.add('smnum__domino--wrong');
    lives--;
    window.setTimeout(() => {
      if (lives <= 0) onLose();
      else advanceAfterMistake();
    }, 600);
  }

  function advance(): void {
    subWithinData++;
    if (subWithinData >= parseInt(level.data[dataIdx]!.sublevels, 10)) {
      subWithinData = 0;
      dataIdx++;
    }
    window.setTimeout(() => {
      if (dataIdx >= level.data.length) {
        finishLevel();
      } else {
        renderProgress();
        spawnDomino();
      }
    }, 400);
  }

  function advanceAfterMistake(): void {
    // After mistake, count is not incremented but we still advance to next domino
    renderProgress();
    spawnDomino();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function onLose(): void {
    inputBlocked = true;
    clearDomino();
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Você perdeu as 3 vidas</h2>
      <p class="activity-finish__text">Acertou ${correctCount} de ${totalSubs} dominós. Tente de novo!</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        <button class="btn btn--primary" type="button" data-act="retry">Tentar de novo</button>
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    stage.querySelector<HTMLElement>('[data-act="retry"]')!.addEventListener('click', () => {
      window.location.hash = `/smallnumbers2/${level.level}?t=${Date.now()}`;
    });
  }

  function finishLevel(): void {
    inputBlocked = true;
    clearDomino();
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalSubs} dominós.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/smallnumbers2/${level.level + 1}`;
      });
    }
  }

  renderKeypad();
  renderProgress();
  spawnDomino();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      clearDomino();
      ctx.root.innerHTML = '';
    },
  };
}
